import { GolfCard } from "@/components/golf/golf-card";
import { GolfButton } from "@/components/golf/golf-button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ChevronDown, ChevronUp, Target, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { getAnalysisForVideo, getUserAnalysisQuota, isAnalysisAllowed } from "@/lib/analysis";
import { AnalysisError, classifyVideoDetailsError } from '@/lib/analysis/error-types';
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { requestEnhancedVideoAnalysis } from "@/lib/analysis";
import { getVideoDetailsReliably, VideoDetailsProvider } from '@/lib/video/details-provider';
import { getVideoById } from '@/lib/videos';
import { useTelemetryHUD } from '@/hooks/useTelemetryHUD';
import { PoseFrameViewer } from '@/components/pose/PoseFrameViewer';
import { supabase } from '@/integrations/supabase/client';
import { isPaymentsEnabled } from '@/lib/flags';
import { PaywallNotice } from '@/components/ui/paywall-notice';
import { usePremiumFeature } from '@/hooks/usePaymentGate';
import { VideoPlayer } from '@/components/video/video-player';
import { TelemetryHUD } from '@/components/TelemetryHUD';
import { ErrorPanel, useAnalysisErrorPanel } from '@/components/analysis/ErrorPanel';
import { BottomTabs } from '@/mvp/navigation/BottomTabs';

export default function Analyze() {
  // Get URL parameters first (needed for hooks)
  const [searchParams] = useSearchParams();
  const videoId = searchParams.get('videoId');
  const { user } = useAuth();
  
  // S-1003 AC1, AC2: Check premium feature access for AI Analysis
  const { hasAccess: hasAnalysisAccess, requestAccess: requestAnalysisAccess } = usePremiumFeature("AI Analysis", "analyze-page");
  
  const [showDetails, setShowDetails] = useState(false);
  const [showPoseViewer, setShowPoseViewer] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [poseFrames, setPoseFrames] = useState<any[]>([]);
  const [quota, setQuota] = useState<{ used: number; limit: number; remaining: number } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // S-2211: Error panel state
  const { error: analysisError, showError, clearError, hasError } = useAnalysisErrorPanel();
  
  // S-2205: Video and telemetry state
  const [videoData, setVideoData] = useState<any>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // S-2205: Telemetry HUD integration
  const {
    hasTelemetryData,
    isLoading: telemetryLoading,
    error: telemetryError,
    getFrameAtTime,
    isVisible: hudVisible,
    toggleVisible: toggleHUD,
    setVisible: setHUDVisible,
    opacity: hudOpacity,
  } = useTelemetryHUD({
    videoId: videoId || undefined,
    autoLoad: true,
  });

  useEffect(() => {
    if (user) {
      loadQuota();
    }
  }, [user]);

  useEffect(() => {
    if (videoId && user) {
      loadExistingAnalysis();
      loadVideoData();
    }
  }, [videoId, user]);

  const loadQuota = async () => {
    try {
      const quotaData = await getUserAnalysisQuota();
      setQuota(quotaData);
    } catch (error) {
      console.error('Failed to load quota:', error);
    }
  };

  const loadExistingAnalysis = async () => {
    if (!videoId) return;
    
    try {
      const analysis = await getAnalysisForVideo(videoId);
      if (analysis) {
        setAnalysisData({
          overallScore: analysis.overall_score,
          breakdown: {
            tempo: analysis.tempo_score,
            form: analysis.form_score,
            power: analysis.power_score
          },
          feedback: analysis.feedback ? analysis.feedback.split('\n\n') : [],
          recommendations: analysis.recommendations ? [analysis.recommendations] : []
        });
      }
    } catch (error) {
      console.error('Failed to load analysis:', error);
    }
  };

  // S-2205: Load video data for telemetry HUD
  const loadVideoData = async () => {
    if (!videoId) return;
    
    try {
      const { data: video, error } = await getVideoById(videoId);
      if (error) {
        console.error('Failed to load video data:', error);
        return;
      }
      
      if (video) {
        setVideoData(video);
      }
    } catch (error) {
      console.error('Failed to load video data:', error);
    }
  };

  const startAnalysis = async () => {
    // S-1003 AC1: Check premium access before allowing analysis
    if (!hasAnalysisAccess) {
      requestAnalysisAccess();
      return;
    }
    
    if (!videoId || !user) {
      toast.error("Please record a video first");
      return;
    }

    // S-2201: Only check quota limits if entitlements gating is enabled
    if (!isAnalysisAllowed() && quota && quota.remaining <= 0) {
      toast.error("You've reached your monthly analysis limit");
      return;
    }

    // S-2211: Clear any previous errors
    clearError();
    setIsAnalyzing(true);

    try {
      // S-2213: Use reliable video details provider with retry + fallback
      const { data: video, error, fromFallback } = await getVideoDetailsReliably(videoId);
      
      // S-2226: Properly classify video readiness errors
      if (fromFallback || !video?.mux_playback_id) {
        if (error) {
          // Use proper error classification instead of treating all as VIDEO_NOT_READY
          const classifiedError = classifyVideoDetailsError(error);
          console.error('Video details error for analysis:', classifiedError.message);
          showError(classifiedError);
        } else {
          // No specific error but video not available
          showError(AnalysisError.videoNotReady('Video not available'));
        }
        return;
      }

      // S-2210: Use enhanced analysis with full frame capture
      const result = await requestEnhancedVideoAnalysis({
        playback_id: video.mux_playback_id,
        video_id: videoId,
        extractionConfig: {
          strategy: 'fps',
          targetFps: 15, // Capture more frames than legacy 10-frame limit
          compressionQuality: 0.7,
          maxWidth: 1280,
          maxHeight: 720
        },
        telemetry: hasTelemetryData ? getFrameAtTime : undefined, // Include telemetry if available
        duration: video.duration
      });

      if (result) {
        setAnalysisData({
          overallScore: result.scores.overall,
          breakdown: {
            tempo: result.scores.tempo,
            form: result.scores.form,
            power: result.scores.power
          },
          feedback: result.bullets,
          recommendations: []
        });

        await loadQuota(); // Refresh quota
        toast.success("Analysis completed successfully!");
      } else {
        showError(AnalysisError.modelError('Analysis completed but returned no results'));
      }
      
    } catch (error: any) {
      console.error('Analysis failed:', error);
      
      // S-2237: Handle CORS/network watchdog failures
      if (error instanceof AnalysisError && error.type === 'NETWORK_OR_CORS_FAILURE') {
        showError(error); // Use the error's built-in user message
        return;
      }
      
      // S-2211: Handle other structured errors
      if (error instanceof AnalysisError) {
        showError(error);
      } else if (error.message?.includes('quota_exceeded') || error.message?.includes('limit')) {
        showError(AnalysisError.quotaExceeded());
      } else {
        showError(AnalysisError.unknown(error));
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadPoseFrames = async (analysisId: string) => {
    try {
      const { data: frames, error } = await supabase
        .from('swing_frames')
        .select('*')
        .eq('analysis_id', analysisId)
        .order('frame_index');

      if (error) throw error;
      setPoseFrames(frames || []);
    } catch (error) {
      console.error('Failed to load pose frames:', error);
    }
  };

  // Show analysis request UI if no analysis exists
  if (!analysisData && videoId) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">AI Swing Analysis</h1>
            <p className="text-muted-foreground">Get expert feedback on your golf swing</p>
          </div>

          {quota && (
            <GolfCard className="text-center">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Monthly Analyses: {quota.used} / {quota.limit} used
                </p>
                <Progress value={(quota.used / quota.limit) * 100} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  {quota.remaining} analyses remaining this month
                </p>
              </div>
            </GolfCard>
          )}

          {/* S-2211: Show error panel if analysis failed */}
          {/* PLA-143: V3 Upload entry point */}
          {hasError && analysisError && (
            <ErrorPanel
              error={analysisError}
              onRetry={startAnalysis}
              onRecordNew={() => window.location.href = '/mvp/upload'}
              onUpgrade={() => requestAnalysisAccess()}
              onShowTips={() => {
                toast.info("Recording Tips: Use good lighting, keep your full body visible, and ensure a stable camera position for best results.");
              }}
            />
          )}

          <GolfCard className="text-center space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Ready for Analysis</h3>
              <p className="text-sm text-muted-foreground">
                Our AI will analyze your swing and provide personalized feedback
              </p>
            </div>
            
{/* S-1003: Use new payment gate system */}
            {hasAnalysisAccess ? (
              <div data-testid="premium-surface">
                {isAnalyzing ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">Analyzing swing...</span>
                    </div>
                    <p className="text-xs text-muted-foreground">This may take 10-30 seconds</p>
                  </div>
                ) : (
                  <GolfButton 
                    onClick={startAnalysis}
                    disabled={!isAnalysisAllowed() && (!quota || quota.remaining <= 0)}
                    className="w-full"
                  >
                    {!isAnalysisAllowed() && quota && quota.remaining <= 0 ? 'Monthly Limit Reached' : 'Start AI Analysis'}
                  </GolfButton>
                )}
              </div>
            ) : (
              <PaywallNotice feature="AI Analysis" />
            )}
          </GolfCard>
        </div>
      </div>
    );
  }

  // Show placeholder if no video ID
  if (!analysisData) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">AI Swing Analysis</h1>
            <p className="text-muted-foreground">Record a video to get started</p>
          </div>
          
          <GolfCard className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto" />
            <div className="space-y-2">
              <h3 className="text-lg font-medium">No Video Selected</h3>
              <p className="text-sm text-muted-foreground">
                Please record a swing video first to receive AI analysis
              </p>
            </div>
            {/* PLA-143: V3 Upload entry point */}
            <GolfButton onClick={() => window.location.href = '/mvp/upload'}>
              Record Video
            </GolfButton>
          </GolfCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-6 py-4 flex items-center justify-center h-20 shadow-sm">
        <img 
          src="/nodoubles_logo_trans.png" 
          alt="No Doubles Golf Co" 
          className="h-12 object-contain"
        />
      </header>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Page Title */}
          <div className="text-center space-y-2">
            <h1 className="text-xl sm:text-2xl font-semibold text-foreground">Swing Analysis Results</h1>
          </div>

        {/* S-2205: Video Player with Telemetry HUD + V2 Toggle */}
        {videoData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          >
            <GolfCard className="overflow-hidden shadow-lg">
              <div className="relative">
                {/* V2: Telemetry Toggle Button */}
                <motion.button
                  onClick={toggleHUD}
                  className="absolute top-4 right-4 z-10 bg-surface-elevated hover:bg-surface shadow-md hover:shadow-lg p-3 rounded-lg transition-all duration-350 border border-border/50"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={hudVisible ? "Hide Telemetry Overlay" : "Show Telemetry Overlay"}
                >
                  {hudVisible ? (
                    <Eye className="w-5 h-5 text-fairway" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-muted-foreground" />
                  )}
                </motion.button>
                
                <VideoPlayer
                  video={videoData}
                  className="w-full"
                  maintainAspectRatio={true}
                  noWrapper={true}
                  ref={videoRef}
                />
                <TelemetryHUD
                  videoRef={videoRef}
                  getFrameAtTime={getFrameAtTime}
                  hasTelemetryData={hasTelemetryData}
                  visible={hudVisible}
                  onVisibilityChange={setHUDVisible}
                  opacity={hudOpacity}
                  className="absolute inset-0"
                />
              </div>
            
            {/* Telemetry Status */}
            {telemetryLoading && (
              <div className="p-4 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading telemetry data...</span>
                </div>
              </div>
            )}
            
            {telemetryError && (
              <div className="p-4 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4" />
                  <span>Telemetry Error: {telemetryError}</span>
                </div>
              </div>
            )}
          </GolfCard>
          </motion.div>
        )}

        {/* V2: Overall Score with stagger animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: 0.1 }}
        >
          <GolfCard className="text-center shadow-md bg-surface-elevated">
            <div className="space-y-6 p-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.2 }}
              >
                <div className={`text-5xl font-bold mb-3 ${
                  analysisData.overallScore >= 80 ? 'text-success' :
                  analysisData.overallScore >= 60 ? 'text-warning' :
                  'text-info'
                }`}>
                  {analysisData.overallScore}
                </div>
                <p className="text-sm text-muted-foreground font-medium">Overall Score</p>
              </motion.div>
              
              <Separator />
              
              <div className="grid grid-cols-3 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.25 }}
                >
                  <div className={`text-3xl font-semibold mb-1 ${
                    analysisData.breakdown.tempo >= 80 ? 'text-success' :
                    analysisData.breakdown.tempo >= 60 ? 'text-warning' :
                    'text-info'
                  }`}>
                    {analysisData.breakdown.tempo}
                  </div>
                  <p className="text-xs text-muted-foreground font-medium">Tempo</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.3 }}
                >
                  <div className={`text-3xl font-semibold mb-1 ${
                    analysisData.breakdown.form >= 80 ? 'text-success' :
                    analysisData.breakdown.form >= 60 ? 'text-warning' :
                    'text-info'
                  }`}>
                    {analysisData.breakdown.form}
                  </div>
                  <p className="text-xs text-muted-foreground font-medium">Form</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.35 }}
                >
                  <div className={`text-3xl font-semibold mb-1 ${
                    analysisData.breakdown.power >= 80 ? 'text-success' :
                    analysisData.breakdown.power >= 60 ? 'text-warning' :
                    'text-info'
                  }`}>
                    {analysisData.breakdown.power}
                  </div>
                  <p className="text-xs text-muted-foreground font-medium">Power</p>
                </motion.div>
              </div>
            </div>
          </GolfCard>
        </motion.div>

        {/* V2: AI Feedback with stagger */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: 0.15 }}
        >
          <GolfCard className="shadow-md bg-surface-elevated">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground">AI Coaching Feedback</h3>
                <Badge variant="secondary" className="shadow-sm">Coach Verified</Badge>
              </div>
              
              <motion.div 
                className="space-y-3"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.4
                    }
                  }
                }}
              >
                {analysisData.feedback.map((feedback: string, index: number) => (
                  <motion.div 
                    key={index} 
                    className="flex gap-3"
                    variants={{
                      hidden: { opacity: 0, x: -20 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] }
                      }
                    }}
                  >
                    <div className="w-7 h-7 bg-fairway/10 text-fairway rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5 shadow-sm">
                      {index + 1}
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">
                      {feedback}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </GolfCard>
        </motion.div>

        {/* Enhanced Pose Analysis */}
        {analysisData.pose && (
          <GolfCard className="border-blue-200 bg-blue-50/50">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">Enhanced Pose Analysis</h3>
                </div>
                <Badge variant="outline" className="border-blue-200 text-blue-600">
                  {analysisData.pose.frameCount} frames analyzed
                </Badge>
              </div>
              
              <div className="space-y-3">
                <p className="text-sm text-foreground leading-relaxed">
                  {analysisData.pose.feedback}
                </p>
              </div>

              {poseFrames.length > 0 && (
                <div className="space-y-3">
                  <GolfButton 
                    variant="outline" 
                    onClick={() => setShowPoseViewer(!showPoseViewer)}
                    className="w-full"
                  >
                    {showPoseViewer ? 'Hide' : 'View'} Pose Visualization
                  </GolfButton>
                  
                  {showPoseViewer && (
                    <div className="mt-4">
                      <PoseFrameViewer frames={poseFrames} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </GolfCard>
        )}

        {/* Recommended Drills */}
        {analysisData.recommendations.length > 0 && (
          <GolfCard className="border-primary/20 bg-primary/5">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Recommended Practice</h3>
              </div>
              
              <div className="space-y-2">
                {analysisData.recommendations.map((rec: string, index: number) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-foreground">{rec}</p>
                  </div>
                ))}
              </div>
              
              <GolfButton className="w-full" onClick={() => window.location.href = '/practice'}>
                Add to Practice Plan
              </GolfButton>
            </div>
          </GolfCard>
        )}

        {/* Detailed Analysis */}
        <GolfCard>
          <div className="space-y-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-lg font-semibold">Detailed Analysis</h3>
              {showDetails ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            
            {showDetails && (
              <div className="space-y-4 pt-2">
                <Separator />
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Tempo Analysis</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Backswing Speed</span>
                        <Progress value={analysisData.breakdown.tempo} className="w-24 h-2" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Transition</span>
                        <Progress value={analysisData.breakdown.tempo - 5} className="w-24 h-2" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Downswing</span>
                        <Progress value={analysisData.breakdown.tempo + 3} className="w-24 h-2" />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Form Analysis</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Setup Position</span>
                        <Progress value={analysisData.breakdown.form + 2} className="w-24 h-2" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Spine Angle</span>
                        <Progress value={analysisData.breakdown.form} className="w-24 h-2" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Club Path</span>
                        <Progress value={analysisData.breakdown.form - 3} className="w-24 h-2" />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Power Analysis</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Hip Rotation</span>
                        <Progress value={analysisData.breakdown.power} className="w-24 h-2" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Weight Transfer</span>
                        <Progress value={analysisData.breakdown.power + 5} className="w-24 h-2" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Follow Through</span>
                        <Progress value={analysisData.breakdown.power - 2} className="w-24 h-2" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </GolfCard>

        {/* Actions */}
        <div className="space-y-3">
          {/* PLA-143: V3 Upload entry point */}
          <GolfButton variant="outline" className="w-full" onClick={() => window.location.href = '/mvp/upload'}>
            Record Another Swing
          </GolfButton>
          <GolfButton variant="outline" className="w-full" onClick={() => window.location.href = '/practice'}>
            View Practice Plans
          </GolfButton>
        </div>
        </div>
      </div>
      <BottomTabs />
    </div>
  );
}